import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { toast } from "../../../../../components/toastNotification";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import useDeleteDataConnection from "../hooks/useDeleteDataConnection";
import useGetDataConnectionList from "../hooks/useGetDataConnection";
import {
  addStateDataConnection,
  dataConnectionFetching,
  dataConnectionFilterText,
  dataConnectionReFetching,
  editStateDataConnection,
  rowEditDataConnection,
  selectedRowsStateDataConnection,
} from "../atoms/atoms";
import { IDataCon, IDataConTableData } from "../types";
import Timer from "components/Timer";
import TableFilters from "components/TableFilters";
import DataConnectionForm from "../form/DataConnectionForm";

export const DataConnectionTable = () => {
  const { data: directData, refetch: reFetch } = useGetDataConnectionList();

  const BulkDelete = useDeleteDataConnection();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(
    addStateDataConnection
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateDataConnection
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateDataConnection
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    dataConnectionFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditDataConnection);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(dataConnectionFetching);
  const isReFetching = useRecoilValue(dataConnectionReFetching);

  //Local States
  const [tableData, setTableData] = useState<IDataCon[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<IDataCon[]>([]);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_securityconfiguration_dataconnection_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_securityconfiguration_dataconnection_delete;
  const editPermission =
    PERMISSIONS.permission_utilities_securityconfiguration_dataconnection_edit;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  // Get Data
  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status === 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: IDataCon[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].dataConnectionName);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  //For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.dataConnectionName &&
        item.dataConnectionName
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );
  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filtereditems);
    }
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: IDataConTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: IDataConTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.dataConnectionId);
    BulkDelete(ids);
    setSelectedRows([]);
    setRecent("");
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Data Connections" : "Data Connection"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Data Connection Name",
      field: "dataConnectionName",
      customSort: (a, b) =>
        a.dataConnectionName && b.dataConnectionName
          ? a.dataConnectionName
              .trim()
              .localeCompare(b.dataConnectionName.trim())
          : a.dataConnectionName
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <DataConnectionForm setRecent={setRecent} />
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              textInputName={"DataConnectionName"}
              columnDetails={"Data Connection Name"}
              filterText={setFilterTextState}
              recentText={recent}
              setRecent={setRecent}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />

            <div className="">
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Data Connection"
                  onClick={() => handleAdd()}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : (
                ""
              )}
            </div>
          </div>
          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>
          <div className="">
            <MaterialTableTS
              data={
                isFetching
                  ? []
                  : filterTextState === "" && recent === ""
                  ? tableData
                  : filterTextState === ""
                  ? tableFilteredData
                  : filtereditems
              }
              showTitle={true}
              columns={matColumns}
              isLoading={isFetching}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Data Connection"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};

export default DataConnectionTable;
